import { Roles, BackOfficeRoles } from 'types';

import { VIEWER_ROLES } from './definitions';

// PAYROLL DASHBOARD (plan §5, §17 Q4): who opens /payroll and what each role
// may start from it. Actions keep today's frontend gates exactly (plan F6):
// Pay / Create Paycheck / Issue Bonus = Company Owner only; New Advance =
// Company Owner and Administrative (owner 26 Oct).

/** Company Owner, Administrative, Accounting: who approves reimbursements
 * from the dashboard (today's gate). Who opens it is the `payroll` tick. */
export const isPayrollRole = (role?: string | null) =>
  Boolean(role && VIEWER_ROLES.includes(role));

/** The route and the sidebar: the role's `payroll` tick (owner 2 Nov 2026,
 * Company Settings > Permissions; it was these three roles). */
export const canOpenPayroll = (features?: readonly string[] | null) =>
  Boolean(features?.includes('payroll'));

/** Today's New Bonus is Company Owner only (profile bonuses/list.tsx). */
export const canIssueBonus = (role?: string | null) =>
  role === Roles.COMPANY_OWNER;

/** The dashboard's New Advance (owner 26 Oct): Company Owner and
 * Administrative, not Accounting. Today's create dialog, unchanged; an
 * advance is given, never requested (no decision buttons, tabs/actions). */
export const canCreateAdvance = (role?: string | null) =>
  role === Roles.COMPANY_OWNER || role === BackOfficeRoles.ADMINISTRATIVE;

type Approvals = {
  misc?: number;
  count?: number;
  bonuses?: number;
  advances?: number;
  reimbursements: number;
  bonusAdvanceMisc: number;
};

/**
 * Where "Approve Pending" opens (SaaS parity #24): Reimbursements (every
 * viewer approves those) while any wait; else, for the Company Owner,
 * Bonuses when any wait (Ops' split counts); else Reimbursements, today's
 * fallback. Never Advances (owner 26 Oct: an advance is never a request,
 * nothing on that tab is approved), and Misc items are on no tab: they
 * stay in each employee's Profile. An older Ops without the split:
 * Bonuses when its combined count is above 0, as before.
 */
export const approvePendingTab = (
  role: string | null | undefined,
  approvals?: Approvals
) => {
  if (role !== Roles.COMPANY_OWNER || approvals?.reimbursements) {
    return 'reimbursements';
  }
  if (approvals?.bonuses === undefined) {
    return (approvals?.bonusAdvanceMisc ?? 0) > 0
      ? 'bonuses'
      : 'reimbursements';
  }
  return approvals.bonuses > 0 ? 'bonuses' : 'reimbursements';
};

/** The items Approve Pending can reach from /payroll: Reimbursements and
 * Bonuses (advances are never approved, Misc is on no tab). An older Ops
 * without the split sends only its combined count. */
export const approvableCount = (approvals?: Approvals & { count: number }) =>
  approvals
    ? approvals.bonuses === undefined
      ? approvals.count
      : approvals.reimbursements + approvals.bonuses
    : 0;
